<template>

    <transition name="dropdown">
        <div v-show="show" id="builder-formatter" :style="styles">
            <div class="controls">
                <a href="#" title="Bold" class="item" :class="{ active: isBold }" @click.prevent="formatText('bold')">
                    <i class="fas fa-bold"></i>
                </a>
                <a href="#" title="Italicize" class="item" :class="{ active: isItalic }" @click.prevent="formatText('italic')">
                    <i class="fas fa-italic"></i>
                </a>
                <a href="#" title="Underline" class="item" :class="{ active: isUnderline }" @click.prevent="formatText('underline')">
                    <i class="fas fa-underline"></i>
                </a>
                <a href="#" title="Strikethrough" class="item" :class="{ active: isStrikethrough }" @click.prevent="formatText('strikethrough')">
                    <i class="fas fa-strikethrough"></i>
                </a>
                <a href="#" title="Hyperlink" class="item" :class="{ active: isLink }" @click.prevent="formatLink">
                    <i class="fas fa-link"></i>
                </a>
                <a href="#" title="Text Color" class="item" @click.prevent="formatFont">
                    <i class="fas fa-paint-brush"></i>
                </a>
                <a href="#" title="Clear Formatting" class="item" @click.prevent="clearFormatting">
                    <i class="fas fa-remove-format"></i>
                </a>
            </div>
        </div>
    </transition>

</template>

<style scoped lang="scss">

    #builder-formatter {

        background: $color-text;
        border-radius: 5px;
        left: 0;
        position: fixed;
        top: 0;
        transition: all .2s;
        width: calc(36px * 7);
        z-index: 100;

        &::after {
            bottom: -6px;
            content: "";
            border-top: 6px solid $color-text;
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            display: block;
            left: 50%;
            margin-left: -6px;
            position: absolute;
        }

        .controls {
            border-radius: 5px;
            display: flex;
            overflow: hidden;
            .item {
                align-items: center;
                display: flex;
                flex-grow: 1;
                height: 40px;
                justify-content: center;
                opacity: 0.8;
                transition: .3s opacity;
                width: 100%;
                &:hover, &.active {
                    opacity: 1;
                }
                &.active i {
                    color: $color-success;
                }
                i {
                    color: #fff;
                    font-size: 14px;
                }
            }
        }

    }

</style>

<script>

import BuilderUtil from './utils/BuilderUtil';

export default {

    data() {
        return {
            range: null,
            show: false,
            styles: {},
            isBold: false,
            isItalic: false,
            isUnderline: false,
            isStrikethrough: false,
            isLink: false,
        }
    },

    computed: {

        modal() {
            return this.$store.getters['builder/selectedModal'];
        },

    },

    mounted() {
        // Add listeners.
        window.addEventListener('keyup', this.onKeyUp);
        window.addEventListener('mouseup', this.onMouseUp);
        window.addEventListener('keydown', this.onKeyDown);
    },

    beforeDestroy() {
        // Remove listeners.
        window.removeEventListener('keyup', this.onKeyUp);
        window.removeEventListener('mouseup', this.onMouseUp);
        window.removeEventListener('keydown', this.onKeyDown);
    },

    methods: {

        /**
         * Event handling.
         */

        onMouseUp(e) {
            this.range = BuilderUtil.selectRange();
            // Hide menu.
            let sel = window.getSelection();
            if (!sel || sel.toString().length === 0) {
                this.hideMenu();
                return;
            };
            // Hide or show menu.
            this.toggleMenu();
        },

        onKeyUp(e) {
            // Hide menu.
            let sel = window.getSelection();
            if (!sel || sel.toString().length === 0) {
                this.hideMenu();
                return;
            };
            // Hide or show menu.
            this.toggleMenu();
        },

        onKeyDown(e) {
            if (e.metaKey && e.keyCode === 66) {
                e.preventDefault();
                this.isBold = !document.queryCommandState('bold');
                this.formatText('bold');
            } else if (e.metaKey && e.keyCode === 73) {
                e.preventDefault();
                this.isItalic = !document.queryCommandState('italic');
                this.formatText('italic');
            } else if (e.metaKey && e.keyCode === 85) {
                e.preventDefault();
                this.isUnderline = !document.queryCommandState('underline');
                this.formatText('underline');
            } else if (e.metaKey && e.keyCode === 85) {
                e.preventDefault();
                this.isStrikethrough = !document.queryCommandState('strikethrough');
                this.formatText('strikethrough');
            }
        },

        /**
         * Formatter methods.
         */

        toggleMenu() {
            let sel = window.getSelection();
            let blockElement = sel.focusNode?.parentElement?.closest('[contenteditable]');
            let blockElementContent = sel.focusNode?.parentElement?.closest('.block-content');
            if (blockElement && blockElementContent) {
                this.range = sel.getRangeAt(0);
                if (sel.toString().length > 0) {
                    let rectSel = sel.getRangeAt(0).getBoundingClientRect();
                    let x = Math.max(20, rectSel.left + (rectSel.width / 2) - (240 / 2));
                    let y = rectSel.top - 50;
                    this.styles = { left: x + 'px', top: y + 'px' };
                    this.showMenu();
                } else {
                    this.hideMenu();
                }
            } else {
                this.hideMenu();
            }
        },

        showMenu() {
            if (this.modal) return;
            this.updateMenu();
            this.show = true;
        },

        hideMenu() {
            this.show = false;
        },

        updateMenu() {
            this.isBold = document.queryCommandState('bold');
            this.isItalic = document.queryCommandState('italic');
            this.isUnderline = document.queryCommandState('underline');
            this.isStrikethrough = document.queryCommandState('strikethrough');
            // Is link selected?
            if (this.range && (
                this.range.startContainer?.parentNode.closest('a') ||
                this.range.endContainer?.parentNode.closest('a'))) {
                this.isLink = true;
            } else {
                this.isLink = false;
            }
        },

        formatLink() {
            if (this.isLink) {
                // Remove link.
                document.execCommand('unlink', false);
                this.updateMenu();
            } else {
                // Prompt to enter link.
                this.$store.commit('builder/setSelectedModalParams', {
                    link: '',
                    range: this.range,
                });
                this.$store.commit('builder/setSelectedModal', 'link');
                this.hideMenu();
            }
        },

        formatFont() {
            // Prompt to enter font.
            this.$store.commit('builder/setSelectedModalParams', {
                range: this.range,
            });
            this.$store.commit('builder/setSelectedModal', 'font');
            this.hideMenu();
        },

        formatText(style) {
            document.execCommand(style);
            this.updateMenu();
        },

        clearFormatting() {
            document.execCommand('unlink', false);
            document.execCommand('removeFormat', false);
            this.updateMenu();
        },

        clearFormattingV2() {
            const parent = this.range.startContainer?.parentNode;
            if (parent && ['A', 'B', 'EM', 'FONT', 'SPAN', 'U'].includes(parent.tagName)) {
                let str = parent.innerText;
                parent.remove();
                this.range.insertNode(document.createTextNode(str));
            }
        },

    },

}

</script>